/**
 * A numeric form input that commits when the edit ends, not on every keystroke.
 *
 * ## The bug this exists to stop
 *
 * The obvious way to write a numeric field — `value={format(stored)}` with an
 * `onChange` that returns early on text it cannot parse — fights the person
 * typing. React re-renders from `stored`, so every keystroke the parser rejects
 * is immediately repainted as the old text: the field cannot be backspaced to
 * empty, `1.` on the way to `1.4` snaps back to `1`, and a field that reformats
 * per keystroke moves the caret out from under the cursor. The value on screen
 * is right and the field is unusable.
 *
 * So this holds a **draft string** from the first keystroke until the edit ends,
 * the same contract {@link NumberCell} gives the ingredient grid:
 *
 * - **Commit on blur or Enter.** Not per keystroke: `1` on the way to `12` is a
 *   value the reducer would happily store and fill the undo stack with.
 * - **Escape reverts** to the stored value and leaves the field.
 * - **A rejected value reverts.** The field never keeps text the document does
 *   not contain, so what is on screen is always what is stored.
 *
 * The revert is *silent*, where `NumberCell` toasts. A grid cell is the only
 * place its number appears, so a silent revert there would look like the edit
 * had been lost; these fields sit next to the readout they feed — the target pH
 * beside the predicted pH, the ions beside the profile summary — where the
 * revert is self-evident. Staying free of the toast context also keeps this
 * usable anywhere, since `useToast` throws outside a `<ToastProvider>`.
 *
 * `NumberCell` itself is not reused because these are form controls rather than
 * grid cells: they wear the `Input` chrome, and their parsing rules are
 * per-field — a pH is not a quantity and has no unit suffixes to honour.
 */

import { useState } from 'react';
import { Input } from '@/components/ui/input';
import type { ParseResult } from '@/lib/units';

export interface NumberFieldProps {
  /** The stored value. `null` shows empty — see {@link allowEmpty}. */
  readonly value: number | null;
  /** Stored value → the text the field shows when it is not being edited. */
  readonly format: (value: number) => string;
  /** Typed text → a value, or the reason it is not one. Never called with `''`. */
  readonly parse: (text: string) => ParseResult;
  /** Called with the parsed value when the edit commits. */
  readonly onCommit: (value: number) => void;
  /** Called instead of {@link onCommit} when {@link allowEmpty} and the field is cleared. */
  readonly onClear?: () => void;
  /** Empty input clears via {@link onClear} instead of reverting. */
  readonly allowEmpty?: boolean;
  /**
   * Called for every keystroke that parses, for a preview that should track the
   * typing rather than wait for the blur. The field still holds its draft, so
   * the text is not reformatted underneath the caret.
   */
  readonly onType?: (value: number) => void;
  /** The accessible name; these fields are labelled by an icon or a heading. */
  readonly label: string;
  readonly id?: string;
  readonly className?: string;
  readonly placeholder?: string;
  readonly disabled?: boolean;
  /** Which on-screen keyboard to ask for. Mass fields use `massInputMode`. */
  readonly inputMode?: 'decimal' | 'text';
}

export function NumberField({
  value,
  format,
  parse,
  onCommit,
  onClear,
  allowEmpty = false,
  onType,
  label,
  id,
  className,
  placeholder,
  disabled = false,
  inputMode = 'decimal',
}: NumberFieldProps) {
  const [draft, setDraft] = useState<string | null>(null);

  const stored = value === null ? '' : format(value);
  const text = draft ?? stored;

  function commit(): void {
    if (draft === null) return;
    const next = draft.trim();
    setDraft(null);
    // Nothing typed, or typed back to what is already there: not an edit.
    if (next === stored) return;
    if (next === '') {
      if (allowEmpty && value !== null) onClear?.();
      return;
    }
    const parsed = parse(next);
    if (parsed.ok) onCommit(parsed.value);
  }

  return (
    <Input
      type="text"
      inputMode={inputMode}
      id={id}
      aria-label={label}
      disabled={disabled}
      placeholder={placeholder}
      className={className}
      value={text}
      onChange={(event) => {
        const typed = event.target.value;
        setDraft(typed);
        if (onType === undefined) return;
        const parsed = parse(typed.trim());
        if (parsed.ok) onType(parsed.value);
      }}
      onBlur={commit}
      onKeyDown={(event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          commit();
        } else if (event.key === 'Escape') {
          event.preventDefault();
          setDraft(null);
          event.currentTarget.blur();
        }
      }}
    />
  );
}
